import React, { useState, useEffect, memo, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { shallowEqual } from 'react-redux';
import { Stack, Text } from 'tamagui';
import { setSkillSettings } from 'reducers/slices/skillsettings';
import { MultipleSelect } from 'components/utility/Buttons';
import Loading from 'components/utility/Loading';
import {
  useGetCategoriesQuery,
  useGetBasicCategoriesQuery,
} from 'utils/RTKGraphQL';

type RootState = {
  skillsettings: {
    cats: number[];
  };
};

type Category = {
  value: number;
  label: string;
};

interface CatsModalProps {
  taxonId: number;
}

function CatsModalChild({ taxonId }: CatsModalProps) {
  const dispatch = useDispatch();
  const reduxCategories = useSelector(
    (state: RootState) => state.skillsettings.cats,
    shallowEqual
  );

  const {
    data: categories,
    status,
    error,
  } = useGetBasicCategoriesQuery(taxonId);

  const {
    data: allCategories,
    isLoading,
    error: catQueryError,
  } = useGetCategoriesQuery(taxonId);

  // Initialize selectedCategories with reduxCategories that belong to this taxon
  const [selectedCategories, setSelectedCategories] = useState<number[]>(() => {
    if (categories) {
      return reduxCategories.filter((catId) =>
        categories.some((cat) => cat.id === catId)
      );
    }
    return [];
  });

  // Initialize when categories load
  useEffect(() => {
    if (categories && reduxCategories.length > 0) {
      const validCategories = reduxCategories.filter((catId) =>
        categories.some((cat) => cat.id === catId)
      );
      setSelectedCategories(validCategories);
    }
  }, [categories]);

  const categoryChange = useCallback(
    (value: Category) => {
      const newSelection = selectedCategories.includes(value.value)
        ? selectedCategories.filter((id) => id !== value.value)
        : [...selectedCategories, value.value];

      // Get current categories from other taxons
      const otherTaxonCategories = allCategories
        ? reduxCategories.filter(
            (catId) => !allCategories.some((cat) => cat.id === catId)
          )
        : reduxCategories;

      // Combine categories
      const updatedCategories = Array.from(
        new Set([...otherTaxonCategories, ...newSelection])
      );

      // Update both local and Redux state
      setSelectedCategories(newSelection);
      dispatch(setSkillSettings({ cats: updatedCategories }));
    },
    [selectedCategories, reduxCategories, allCategories, dispatch]
  );

  if (status === 'pending' || isLoading) {
    return (
      <Stack alignItems="center" justifyContent="center" height={200}>
        <Loading />
      </Stack>
    );
  }

  if (status === 'rejected' || catQueryError) {
    console.error('Categories error:', {
      status,
      catQueryError,
      error,
      taxonId,
    });
    return (
      <Stack alignItems="center" justifyContent="center" height={200}>
        <Text>Error loading categories. Please try again later.</Text>
      </Stack>
    );
  }

  if (!categories || categories.length === 0) {
    return (
      <Stack alignItems="center" justifyContent="center" height={200}>
        <Text>No categories available.</Text>
      </Stack>
    );
  }

  return (
    <MultipleSelect
      items={categories.map((cat) => ({
        value: cat.id,
        label: cat.name,
      }))}
      selectedItems={selectedCategories.map((value) => ({
        value,
        label: categories.find((cat) => cat.id === value)?.name || '',
      }))}
      onPress={categoryChange}
    />
  );
}

export const CatsModal = memo(CatsModalChild);
